import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  STATUSES,
  STATUS_META,
  ENTITY_TYPES,
  ENTITY_META,
  DEFAULT_COMMITTEE,
  deriveEntityType,
} from "@/lib/constants";
import { api } from "@/lib/api";
import { toast } from "sonner";

const emptyMember = {
  name: "",
  company: "",
  committee: DEFAULT_COMMITTEE,
  phone: "",
  status: "Beyaz",
  notes: "",
  order_no: "",
  member_no: "",
  trade_reg_no: "",
  address: "",
  entity_type: "Şahıs", // unvan yazıldıkça türetilir; boş unvan şahıs sayılır
};

export default function MemberFormDialog({ open, onOpenChange, member, onSaved }) {
  const [form, setForm] = useState(emptyMember);
  const [saving, setSaving] = useState(false);
  // Kullanıcı türü elle seçtiyse unvana göre otomatik değiştirmeyi bırakıyoruz
  const [turElleSecildi, setTurElleSecildi] = useState(false);
  const isEdit = !!member?.id;

  useEffect(() => {
    if (open) {
      setForm(member ? { ...emptyMember, ...member } : emptyMember);
      // Var olan kayıtta tür zaten belirlenmiş; unvan düzenlense de ezilmesin
      setTurElleSecildi(Boolean(member?.id));
    }
  }, [open, member]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Unvan yazıldıkça şirket türü kendiliğinden gelsin — yardım metni bunu vaat
  // ediyor, oysa tür sabit "Firma" başlayınca şahıs şirketleri yanlış kaydoluyordu.
  const updateCompany = (v) =>
    setForm((f) => ({
      ...f,
      company: v,
      entity_type: turElleSecildi ? f.entity_type : deriveEntityType(v),
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Ad Soyad zorunlu");
      return;
    }
    const orderRaw = String(form.order_no ?? "").trim();
    if (orderRaw !== "" && !/^\d+$/.test(orderRaw)) {
      toast.error("Sıra No yalnızca rakam olabilir");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        committee: form.committee || DEFAULT_COMMITTEE,
        order_no: orderRaw === "" ? null : Number(orderRaw),
      };
      if (isEdit) {
        // Yalnız bu formda değiştirilen alanlar gönderilir. Eskiden bütün form
        // gidiyordu: uzun süre açık kalmış (bayat) bir sekmeden not eklemek,
        // bu arada başkasının değiştirdiği durumu eski hâline çeviriyordu.
        const degisen = {};
        for (const k of Object.keys(emptyMember)) {
          if (String(payload[k] ?? "") !== String(member[k] ?? "")) degisen[k] = payload[k];
        }
        if (Object.keys(degisen).length === 0) {
          toast.info("Değişiklik yapılmadı");
          onOpenChange(false);
          return;
        }
        const updated = await api.updateMember(member.id, degisen);
        toast.success("Üye güncellendi");
        onSaved?.(updated, "update");
      } else {
        const created = await api.createMember(payload);
        toast.success("Üye eklendi");
        onSaved?.(created, "create");
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg max-h-[92vh] overflow-y-auto"
        data-testid="member-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {isEdit ? "Üye Düzenle" : "Yeni Üye Ekle"}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? "Üye bilgilerini güncelleyin." : "Yeni bir üye kartı oluşturun."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name" className="text-xs uppercase tracking-[0.05em] font-semibold">
              Ad Soyad *
            </Label>
            <Input
              id="name"
              data-testid="form-name-input"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Örn. Mehmet Yılmaz"
              className="h-11"
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="company" className="text-xs uppercase tracking-[0.05em] font-semibold">
              Şirket Adı
            </Label>
            <Input
              id="company"
              data-testid="form-company-input"
              value={form.company}
              onChange={(e) => updateCompany(e.target.value)}
              placeholder="Örn. Yılmaz Mobilya"
              className="h-11"
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="order_no" className="text-xs uppercase tracking-[0.05em] font-semibold">
                Sıra No
              </Label>
              <Input
                id="order_no"
                data-testid="form-order-no-input"
                value={form.order_no ?? ""}
                onChange={(e) => update("order_no", e.target.value)}
                placeholder="Örn. 559"
                className="h-11 font-mono"
                inputMode="numeric"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="member_no" className="text-xs uppercase tracking-[0.05em] font-semibold">
                Üye Sicil No
              </Label>
              <Input
                id="member_no"
                data-testid="form-member-no-input"
                value={form.member_no || ""}
                onChange={(e) => update("member_no", e.target.value)}
                placeholder="Örn. 1234"
                className="h-11 font-mono"
                inputMode="numeric"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="trade_reg_no" className="text-xs uppercase tracking-[0.05em] font-semibold">
                Ticaret Sicil No
              </Label>
              <Input
                id="trade_reg_no"
                data-testid="form-trade-reg-no-input"
                value={form.trade_reg_no || ""}
                onChange={(e) => update("trade_reg_no", e.target.value)}
                placeholder="Örn. 5678"
                className="h-11 font-mono"
                inputMode="numeric"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="phone" className="text-xs uppercase tracking-[0.05em] font-semibold">
                Telefon
              </Label>
              <Textarea
                id="phone"
                data-testid="form-phone-input"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder={"05551234567 - Ahmet Bey\n05339876543 - Mehmet Bey"}
                rows={3}
                className="resize-y"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.05em] font-semibold">
                Durum
              </Label>
              <Select value={form.status} onValueChange={(v) => update("status", v)}>
                <SelectTrigger data-testid="form-status-select" className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => {
                    const meta = STATUS_META[s];
                    return (
                      <SelectItem key={s} value={s} data-testid={`form-status-option-${s}`}>
                        <span className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ background: meta.dot }} />
                          {meta.option}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.05em] font-semibold">
              Şirket Türü
            </Label>
            <div className="grid grid-cols-2 gap-2" data-testid="form-entity-type-toggle">
              {ENTITY_TYPES.map((t) => {
                const em = ENTITY_META[t];
                const secili = (form.entity_type || "Firma") === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => {
                      setTurElleSecildi(true);
                      update("entity_type", t);
                    }}
                    aria-pressed={secili}
                    data-testid={`form-entity-option-${em.key}`}
                    className={`rounded-lg border p-3 text-left transition-all ${
                      secili
                        ? `${em.bg} ${em.border} ring-2 ring-offset-1 dark:ring-offset-neutral-950`
                        : "border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600"
                    }`}
                    style={secili ? { "--tw-ring-color": em.dot } : undefined}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 rounded-full shrink-0"
                        style={{ background: em.dot, opacity: secili ? 1 : 0.4 }}
                      />
                      <span
                        className={`font-semibold text-sm ${
                          secili ? em.text : "text-neutral-600 dark:text-neutral-300"
                        }`}
                      >
                        {em.long}
                      </span>
                    </div>
                    <div className="text-[10.5px] text-neutral-500 dark:text-neutral-400 mt-1 leading-snug">
                      {em.vote}
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-neutral-400 dark:text-neutral-500">
              {turElleSecildi
                ? "Elle seçildi; unvan değişse de bu seçim korunur."
                : "Unvana bakılarak otomatik seçiliyor; yanlışsa üstten değiştirin."}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="address" className="text-xs uppercase tracking-[0.05em] font-semibold">
              Adres
            </Label>
            <Textarea
              id="address"
              data-testid="form-address-input"
              value={form.address || ""}
              onChange={(e) => update("address", e.target.value)}
              placeholder="Örn. Cuma Mah. Sanayi Cad. No:12 İnegöl / BURSA"
              rows={2}
              className="resize-y"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes" className="text-xs uppercase tracking-[0.05em] font-semibold">
              Ek Notlar
            </Label>
            <Textarea
              id="notes"
              data-testid="form-notes-input"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Görüşme detayları, tavsiye durumu, hassasiyetler..."
              rows={4}
            />
            <p className="text-[10px] text-neutral-400 dark:text-neutral-500">
              Not kaydedildiğinde tarih otomatik olarak eklenir.
            </p>
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="form-cancel-btn"
              className="h-11"
            >
              İptal
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="form-save-btn"
              className="h-11 bg-neutral-950 hover:bg-neutral-800 text-white dark:bg-neutral-100 dark:text-neutral-950 dark:hover:bg-white"
            >
              {saving ? "Kaydediliyor..." : isEdit ? "Güncelle" : "Ekle"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
